import { appContext } from "../../../../AppContext";
import { JSX } from "react";
import { IbssPage } from "../../../../Components/Core/BasePage/IbssPage";
import { IRouterProps, withRouting } from "@iconics/components";
import { Relationship } from "./DataModels";
import { MultilingualTextField } from "@iconics/components";
import { ILanguageValues, LanguageValuesHelper } from "@iconics/localisation";
import { SerendieSymbol } from "@serendie/symbols";
import { Switch } from "@serendie/ui";
import { Validator } from '@iconics/core';
import { Button, ButtonBar, Card, DeleteButton, GridContainer, GridItem, IPageNavSection, LayoutContainer, LayoutContent, LayoutFooter, LayoutRight, LeftButtons, PageNav, RightButtons, TextField } from "@iconics/components";

class EditRelationship extends IbssPage<IProps, IState>
{
    private validator = new Validator(appContext().core.validationOptions);
    private get labels() { return appContext().labels; }
    private get isNew() { return !this.props.params.id; }
    private languageValuesHelper: LanguageValuesHelper;

    constructor(props: IProps)
    {
        super(props);
        this.languageValuesHelper = new LanguageValuesHelper(appContext().localisation);

        this.state = {
            canDelete: appContext().localStorageProvider.hasRight('DATAMODEL.Relationships.Delete'),
            isEnabled: false,
            name: '',
            nameError: '',
            displayName: this.languageValuesHelper.default(),
            description: '',
            descriptionError: '',
            primary2SecondaryRelation: '',
            primary2SecondaryRelationError: '',
            secondary2PrimaryRelation: '',
            secondary2PrimaryRelationError: '',
            eTag: '',
            sections: [],
        };
    }

    public async componentDidMount(): Promise<void>
    {
        const labels = appContext().labels;
        this.pageTitle = labels.Platform_Relationship_PageTitle;
        this.backUrl = `/admin/relationships`;

        this.validator
            .addInput('name', () => this.state.name, i => this.setState({ nameError: i }))
            .isRequired();
        this.validator
            .addInput('description', () => this.state.description, i => this.setState({ descriptionError: i }))
            .isRequired();
        this.validator
            .addInput('primary2SecondaryRelation', () => this.state.primary2SecondaryRelation, i => this.setState({ primary2SecondaryRelationError: i }))
            .isRequired();
        this.validator
            .addInput('secondary2PrimaryRelation', () => this.state.secondary2PrimaryRelation, i => this.setState({ secondary2PrimaryRelationError: i }))
            .isRequired();

        if (!this.isNew)
        {
            await this.loading(async () =>
            {
                const relationship = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.relationships.byId.get<Relationship>({
                    id: this.props.params.id ?? '',
                    select: Relationship,
                    tenancy: appContext().tenantId,
                });

                this.setState({
                    isEnabled: relationship.IsEnabled,
                    name: relationship.Name,
                    displayName: this.languageValuesHelper.fromString(relationship.DisplayName),
                    description: relationship.Description,
                    primary2SecondaryRelation: relationship.Primary2SecondaryRelation,
                    secondary2PrimaryRelation: relationship.Secondary2PrimaryRelation,
                    eTag: relationship['@odata.etag'],
                });
            });
        }
    }

    private async handleDelete(): Promise<void>
    {
        await this.loading(async () =>
        {
            await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.relationships.byId.delete({
                id: this.props.params.id ?? '',
                tenancy: appContext().tenantId,
            });
            this.props.navigate('/admin/relationships');
        });
    }

    private async handleSave(): Promise<void>
    {
        if (!this.validator.validateAll())
        {
            return;
        }
        await this.loading(async () =>
        {
            if (!this.isNew)
            {
                await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.relationships.byId.patch({
                    body: {
                        IsEnabled: this.state.isEnabled ? 'Yes' : 'No',
                        Name: this.state.name,
                        Description: this.state.description,
                        DisplayName: this.languageValuesHelper.toString(this.state.displayName),
                        Primary2SecondaryRelation: this.state.primary2SecondaryRelation,
                        Secondary2PrimaryRelation: this.state.secondary2PrimaryRelation,
                        '@odata.etag': this.state.eTag,
                    },
                    id: this.props.params.id ?? '',
                    tenancy: appContext().tenantId,
                });
            }
            else
            {
                await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.relationships.post({
                    body: {
                        IsEnabled: this.state.isEnabled ? 'Yes' : 'No',
                        Name: this.state.name,
                        Description: this.state.description,
                        DisplayName: this.languageValuesHelper.toString(this.state.displayName),
                        Primary2SecondaryRelation: this.state.primary2SecondaryRelation,
                        Secondary2PrimaryRelation: this.state.secondary2PrimaryRelation,
                    },
                    tenancy: appContext().tenantId,
                });
            }
            this.props.navigate('/admin/relationships');
        });
    }

    public render(): JSX.Element
    {
        return (
            <LayoutContainer>
                <LayoutContent>
                    <Card
                        title={this.labels.Platform_Relationship_DetailsTitle}
                        subTitle={this.labels.Platform_Relationship_DetailsSubTitle}
                        onSectionUpdate={i => this.setState(prev => ({ sections: PageNav.update(prev.sections, i, 0) }))}
                        icon={<SerendieSymbol name='share-alt' />}
                    >
                        <GridContainer>
                            <GridItem expanded={6}>
                                <TextField
                                    required
                                    label={this.labels.Relationship_Name}
                                    placeholder={this.labels.Relationship_Name}
                                    value={this.state.name}
                                    onChange={e => this.setState({ name: e.target.value }, () => this.validator.validate('name'))}
                                    invalid={!!this.state.nameError}
                                    invalidMessage={this.state.nameError}
                                />
                            </GridItem>
                            <GridItem expanded={6}>
                                <MultilingualTextField
                                    label={this.labels.Relationship_DisplayName}
                                    placeholder={this.labels.Relationship_DisplayName}
                                    value={this.state.displayName}
                                    onChange={value => this.setState({ displayName: value  })}
                                    localisation={appContext().localisation}
                                />
                            </GridItem>
                            <GridItem>
                                <TextField
                                    required
                                    label={this.labels.Relationship_Description}
                                    placeholder={this.labels.Relationship_Description}
                                    value={this.state.description}
                                    onChange={e => this.setState({ description: e.target.value }, () => this.validator.validate('description'))}
                                    invalid={!!this.state.descriptionError}
                                    invalidMessage={this.state.descriptionError}
                                />
                            </GridItem>
                            <GridItem expanded={6}>
                                <TextField
                                    required
                                    label={this.labels.Relationship_Primary2SecondaryRelation}
                                    placeholder={this.labels.Relationship_Primary2SecondaryRelation}
                                    value={this.state.primary2SecondaryRelation}
                                    onChange={e => this.setState({ primary2SecondaryRelation: e.target.value }, () => this.validator.validate('primary2SecondaryRelation'))}
                                    invalid={!!this.state.primary2SecondaryRelationError}
                                    invalidMessage={this.state.primary2SecondaryRelationError}
                                />
                            </GridItem>
                            <GridItem expanded={6}>
                                <TextField
                                    required
                                    label={this.labels.Relationship_Secondary2PrimaryRelation}
                                    placeholder={this.labels.Relationship_Secondary2PrimaryRelation}
                                    value={this.state.secondary2PrimaryRelation}
                                    onChange={e => this.setState({ secondary2PrimaryRelation: e.target.value }, () => this.validator.validate('secondary2PrimaryRelation'))}
                                    invalid={!!this.state.secondary2PrimaryRelationError}
                                    invalidMessage={this.state.secondary2PrimaryRelationError}
                                />
                            </GridItem>
                            <GridItem>
                                <Switch
                                    label={this.labels.Relationship_IsEnabled}
                                    checked={this.state.isEnabled}
                                    onCheckedChange={e => this.setState({ isEnabled: e.checked as boolean })}
                                />
                            </GridItem>
                        </GridContainer>
                    </Card>
                </LayoutContent>
                <LayoutRight>
                    <PageNav
                        sections={this.state.sections}
                    />
                </LayoutRight>
                <LayoutFooter>
                    <ButtonBar>
                        <LeftButtons>
                            {
                                !this.isNew &&
                                <DeleteButton
                                    disabled={!this.state.canDelete}
                                    dialogMessage={this.labels.Platform_Relationship_ConfirmDeleteMsg}
                                    onDelete={() => this.handleDelete()}
                                />
                            }
                        </LeftButtons>
                        <RightButtons>
                            <Button
                                styleType='outlined'
                                onClick={() => this.props.navigate('/admin/relationships')}
                            >
                                {this.labels.Platform_CommonAction_Cancel}
                            </Button>
                            <Button
                                styleType='filled'
                                onClick={() => this.handleSave()}
                                disabled={!this.validator.isValid}
                            >
                                {this.labels.Platform_CommonAction_Save}
                            </Button>
                        </RightButtons>
                    </ButtonBar>
                </LayoutFooter>
            </LayoutContainer>
        );
    }
}

export default withRouting(EditRelationship);

export interface IProps extends IRouterProps<IRouteParams>
{
}

export interface IRouteParams
{
    id?: string;
}

export interface IState
{
    canDelete: boolean;
    isEnabled: boolean;
    name: string;
    nameError: string;
    displayName: ILanguageValues;
    description: string;
    descriptionError: string;
    primary2SecondaryRelation: string;
    primary2SecondaryRelationError: string;
    secondary2PrimaryRelation: string;
    secondary2PrimaryRelationError: string;
    eTag: string;
    sections: IPageNavSection[];
}

export type FieldName = 'Name' | 'Description' | 'Primary2SecondaryRelation' | 'Secondary2PrimaryRelation';
